import * as Sentry from '@sentry/browser';
import * as React from 'react';
import { useState, useEffect, useContext, createContext } from 'react';
import trackEvent from '../../utils/analytics';
import { auth } from '../utils/firebase';
import isBot from 'utils/is-bot';

// This is a hack version of Auth.User for Firebase
// I wasn't able to export the type from the flow-typed libdef
type AuthUser = {
  uid: string;
  isAnonymous: boolean;
};

const authContext = createContext<{ user: AuthUser | false | null }>({
  user: null,
});

// Provider component that wraps your app and makes auth object
// available to any child component that calls useAuth().
export function ProvideAuth({ children }: { children: React.ReactNode }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

// Hook for child components to get the auth object and re-render when it
// changes.
export const useAuth = () => {
  return useContext(authContext);
};

// Provider hook that creates auth object and handles state
function useProvideAuth() {
  // Null is used before any response is received
  // False means that an empty reponse was received
  const [user, setUser] = useState<AuthUser | false | null>(null);

  // Subscribe to user on mount
  // Because this sets state in the callback it will cause any component that
  // utilizes this hook to re-render with the latest auth object.
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUser(user);

        // Add the user's id to Sentry for easier debugging
        if (user && user.uid) {
          Sentry.setUser({
            id: user.uid,
            ip_address: '{{auto}}',
          });
        }

        // Because Mixpanel requires a specific order for the initial
        // Alias/Identify, we skip this if the user was just created.
        if (!isBot() && !isPreAlias(user)) {
          try {
            trackEvent.signedIn(user.uid);
          } catch (error) {}
        }
      } else {
        setUser(false);
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Return the user
  return { user };
}

function isPreAlias(user: any): boolean {
  const now = Date.now();
  const twoMinutes = 1000 * 60 * 2;
  if (user && user.metadata && user.metadata.creationTime) {
    const createdTimestamp = new Date(user.metadata.creationTime).getTime();
    return createdTimestamp + twoMinutes > now;
  }
  return false;
}
